import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import { ArrowLeft, Check, ShieldCheck } from "lucide-react";
import { LegalLayout } from "@/components/LegalLayout";
import { LinkedTerms } from "@/components/LinkedTerms";
import { SHOW_UPPROMOTE_PUBLIC } from "@/lib/linked-terms";

import { Button } from "@/components/ui/button";
import { useLocale } from "@/contexts/LocaleContext";
import { useAuth } from "@/contexts/AuthContext";
import { DICTS } from "@/lib/i18n";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";
import {
  getAmbassadorTermsStatus,
  acceptAmbassadorTerms,
} from "@/lib/ambassador-terms.functions";

const SECTION_IDS = Array.from({ length: 12 }, (_, i) => `s${i + 1}`).filter(
  // s12 (المنصّات الخارجيّة/UpPromote) مخفيّة بقرار الإدارة — انظر SHOW_UPPROMOTE_PUBLIC
  (id) => SHOW_UPPROMOTE_PUBLIC || id !== "s12",
);

export const Route = createFileRoute("/ambassadors/terms")({
  head: () => ({
    meta: [
      { title: `${DICTS.en["ambassadors.terms.title"]} · Lecmo Ambassadors` },
      { name: "description", content: DICTS.en["ambassadors.terms.subtitle"] },
      { property: "og:title", content: DICTS.en["ambassadors.terms.title"] },
      { property: "og:description", content: DICTS.en["ambassadors.terms.subtitle"] },
      { property: "og:url", content: `${PUBLIC_SITE_ORIGIN}/ambassadors/terms` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${PUBLIC_SITE_ORIGIN}/ambassadors/terms` }],
  }),
  component: AmbassadorTermsPage,
});

function AcceptBlock() {
  const { t, locale } = useLocale();
  const { user } = useAuth();
  const qc = useQueryClient();
  const fetchStatus = useServerFn(getAmbassadorTermsStatus);
  const accept = useServerFn(acceptAmbassadorTerms);

  const { data } = useQuery({
    queryKey: ["ambassador-terms-status"],
    queryFn: () => fetchStatus({}),
    enabled: !!user,
  });

  const mutation = useMutation({
    mutationFn: () => accept({ data: { context: "manual" as const } }),
    onSuccess: () => {
      toast.success(t("ambassadors.terms.accepted_toast"));
      qc.invalidateQueries({ queryKey: ["ambassador-terms-status"] });
    },
    onError: () => toast.error(t("ambassadors.terms.error_toast")),
  });

  if (!user) {
    return (
      <div className="not-prose border border-border bg-muted/30 p-4 text-sm">
        <Link to="/auth" search={{ redirect: "/ambassadors/terms", mode: "signin" }} className="underline">
          {t("ambassadors.terms.signin_required")}
        </Link>
      </div>
    );
  }

  const accepted = data && !data.needsAcceptance;

  return (
    <div className="not-prose border border-border bg-card p-4 md:p-5 space-y-3">
      {data?.currentVersion ? (
        <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          {t("ambassadors.terms.last_updated")}: {data.currentVersion}
        </p>
      ) : null}

      {accepted ? (
        <p className="flex items-start gap-2 text-sm text-foreground">
          <Check className="mt-0.5 h-4 w-4 shrink-0" />
          {t("ambassadors.terms.already_accepted", {
            date: new Date(data!.lastAcceptedAt as string).toLocaleDateString(
              locale === "ar" ? "ar-AE" : "en-GB",
            ),
          })}
        </p>
      ) : (
        <>
          {data?.lastAcceptedVersion ? (
            <p className="text-sm text-muted-foreground">
              {t("ambassadors.terms.needs_accept_banner")}
            </p>
          ) : null}
          <p className="text-sm">{t("ambassadors.terms.accept_cta")}</p>
          <Button
            size="sm"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            <ShieldCheck className="me-2 h-4 w-4" />
            {t("ambassadors.terms.accept_btn")}
          </Button>
        </>
      )}
    </div>
  );
}

function AmbassadorTermsPage() {
  const { t } = useLocale();

  return (
    <LegalLayout
      titleKey="ambassadors.terms.title"
      eyebrowKey="ambassadors.terms.eyebrow"
    >
      <p>{t("ambassadors.terms.subtitle")}</p>

      <AcceptBlock />

      <nav className="not-prose my-6 border-s-2 ps-4" style={{ borderColor: "var(--rule-accent)" }}>
        <p className="mb-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          {t("ambassadors.terms.toc")}
        </p>
        <ol className="space-y-1 text-sm">
          {SECTION_IDS.map((id) => (
            <li key={id}>
              <a href={`#${id}`} className="text-muted-foreground hover:text-foreground">
                {t(`ambassadors.terms.${id}.title` as never)}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      {SECTION_IDS.map((id) => (
        <React.Fragment key={id}>
          <h2 id={id}>{t(`ambassadors.terms.${id}.title` as never)}</h2>
          <p>
            <LinkedTerms text={t(`ambassadors.terms.${id}.body` as never)} />
          </p>

        </React.Fragment>
      ))}

      <p className="not-prose mt-8">
        <Link
          to="/ambassadors"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("ambassadors.terms.back_to_program")}
        </Link>
      </p>
    </LegalLayout>
  );
}
